import { useEffect, useRef, useState } from 'react';
import * as signalR from '@microsoft/signalr';
import { NotificationResponse } from '../interfaces/INotification';
import { ICommunityFeedDelta } from '../interfaces/ICommunityFeedDelta';
import config from '../config';

interface UseNotificationHubOptions {
    onNotificationReceived?: (notification: NotificationResponse) => void;
    onUnreadCountUpdated?: (count: number) => void;
    onReconnected?: () => void;
    enabled?: boolean;
    token?: string;
    hubTarget: 'admin' | 'participant';
}

export const useNotificationHub = ({
    onNotificationReceived,
    onUnreadCountUpdated,
    onReconnected,
    enabled = true,
    token,
    hubTarget,
}: UseNotificationHubOptions) => {
    const connectionRef = useRef<signalR.HubConnection | null>(null);
    const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const isMountedRef = useRef(true);
    const retryCountRef = useRef(0);
    const onNotificationReceivedRef = useRef<UseNotificationHubOptions['onNotificationReceived']>(undefined);
    const onUnreadCountUpdatedRef = useRef<UseNotificationHubOptions['onUnreadCountUpdated']>(undefined);
    const onReconnectedRef = useRef<UseNotificationHubOptions['onReconnected']>(undefined);
    const [isConnected, setIsConnected] = useState(false);
    const maxRetries = 10;

    const hubPath = hubTarget === 'participant' ? '/participantNotificationHub' : '/adminNotificationHub';
    const hubName = hubTarget === 'participant' ? 'ParticipantNotificationHub' : 'AdminNotificationHub';

    useEffect(() => {
        onNotificationReceivedRef.current = onNotificationReceived;
    }, [onNotificationReceived]);

    useEffect(() => {
        onUnreadCountUpdatedRef.current = onUnreadCountUpdated;
    }, [onUnreadCountUpdated]);

    useEffect(() => {
        onReconnectedRef.current = onReconnected;
    }, [onReconnected]);

    useEffect(() => {
        isMountedRef.current = true;
        retryCountRef.current = 0;

        if (!enabled || !token) {
            return;
        }

        // Only create connection if it doesn't exist yet
        if (!connectionRef.current) {
            connectionRef.current = new signalR.HubConnectionBuilder()
                .withUrl(`${config.apiServerUrl}${hubPath}`, {
                    accessTokenFactory: () => token,
                    skipNegotiation: false,
                    transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.ServerSentEvents,
                })
                .withAutomaticReconnect([0, 2000, 5000, 10000])
                .configureLogging(signalR.LogLevel.Information)
                .build();
        }

        const connection = connectionRef.current;

        const startConnection = async () => {
            if (!isMountedRef.current) {
                console.log('Component unmounted, aborting SignalR connection attempt');
                return;
            }

            if (connection.state === signalR.HubConnectionState.Connected) {
                console.log(`Already connected to ${hubName}`);
                retryCountRef.current = 0; // Reset on successful connection
                setIsConnected(true);
                return;
            }

            if (connection.state !== signalR.HubConnectionState.Disconnected) {
                return;
            }

            try {
                console.log(`Starting SignalR connection to ${hubName}...`);
                await connection.start();
                console.log(`Connected to ${hubName}`);
                retryCountRef.current = 0; // Reset retry count on success
                setIsConnected(true);
            } catch (error) {
                retryCountRef.current++;

                const errorMessage = String(error ?? '');
                const isNegotiationAbort = errorMessage.includes('stopped during negotiation');
                const isForbidden = errorMessage.includes("Status code '403'") || errorMessage.includes('403 (Forbidden)');
                const isUnauthorized = errorMessage.includes("Status code '401'") || errorMessage.includes('401 (Unauthorized)');

                if (!isMountedRef.current || isNegotiationAbort) {
                    return;
                }

                console.error(`SignalR Connection Error (attempt ${retryCountRef.current}/${maxRetries}):`, error);

                if (isForbidden || isUnauthorized) {
                    console.warn(`Stopping ${hubName} retries due to auth failure.`);
                    return;
                }

                if (retryCountRef.current >= maxRetries) {
                    console.error('Max retry attempts reached. Giving up on SignalR connection.');
                    return;
                }

                if (isMountedRef.current) {
                    // Exponential backoff: 5s, 10s, 20s, 40s, max 60s
                    const delay = Math.min(5000 * Math.pow(2, retryCountRef.current - 1), 60000);
                    console.log(`Retrying in ${delay / 1000}s...`);
                    retryTimeoutRef.current = setTimeout(() => startConnection(), delay);
                }
            }
        };

        startConnection();

        // Register event handlers
        const handleNotificationReceived = (notification: NotificationResponse) => {
            if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('odyssey:notification-received', {
                    detail: notification,
                }));
            }

            onNotificationReceivedRef.current?.(notification);
        };

        const handleFeedDeltaReceived = (delta: ICommunityFeedDelta) => {
            if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('odyssey:feed-delta-received', {
                    detail: delta,
                }));
            }
        };

        connection.on('ReceiveNotification', handleNotificationReceived);
        connection.on('ReceiveFeedDelta', handleFeedDeltaReceived);

        // Comms screens listen for this on window so the hook stays agnostic of what is open
        const handleCommsThreadChanged = (change: unknown) => {
            if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('odyssey:comms-thread-changed', { detail: change }));
            }
        };
        connection.on('CommsThreadChanged', handleCommsThreadChanged);

        const handleUnreadCountUpdated = (count: number) => {
            onUnreadCountUpdatedRef.current?.(count);
        };

        connection.on('UnreadCountUpdated', handleUnreadCountUpdated);

        connection.onreconnected(() => {
            console.log(`SignalR reconnected to ${hubName}`);
            // SignalR does not replay messages missed while the connection was down, so the
            // in-memory unread count/list can drift. Reconcile from the server on every
            // successful reconnect (refetch unread count + notify listeners to refetch lists).
            onReconnectedRef.current?.();
            if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('odyssey:notifications-resync', {
                    detail: { hubTarget },
                }));
            }
        });

        connection.onclose(() => {
            console.log('SignalR connection closed');
            setIsConnected(false);
        });

        return () => {
            isMountedRef.current = false;

            // Cancel any pending retry attempts
            if (retryTimeoutRef.current) {
                clearTimeout(retryTimeoutRef.current);
                retryTimeoutRef.current = null;
            }

            connection.off('ReceiveNotification', handleNotificationReceived);
            connection.off('ReceiveFeedDelta', handleFeedDeltaReceived);
            connection.off('CommsThreadChanged', handleCommsThreadChanged);
            connection.off('UnreadCountUpdated', handleUnreadCountUpdated);
            connection.stop();
            connectionRef.current = null;
            setIsConnected(false);
        };
    }, [token, enabled, hubPath, hubName, hubTarget]);

    return {
        connection: null,
        isConnected,
    };
};
